import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../layouts/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { claimsAPI } from '../lib/api';
import { formatDate, formatCurrency, getStatusClass, downloadBlob } from '../lib/utils';
import {
  ArrowLeft,
  Download,
  Send,
  XCircle,
  Loader2,
  Calendar,
  MapPin,
  Car,
  Users,
  DollarSign,
  CheckCircle,
  Clock
} from 'lucide-react';

export default function ClaimDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchClaim();
  }, [id]);

  const fetchClaim = async () => {
    setLoading(true);
    try {
      const response = await claimsAPI.getClaimDetail(id);
      setClaim(response.data);
    } catch (error) {
      toast.error('Failed to load claim details');
      navigate('/claims');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    setActionLoading(true);
    try {
      await claimsAPI.applyClaim(id);
      toast.success('Claim submitted for approval');
      fetchClaim();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit claim');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this claim?')) return;
    
    setActionLoading(true);
    try {
      await claimsAPI.cancelClaim(id);
      toast.success('Claim cancelled');
      fetchClaim();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel claim');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setActionLoading(true);
    try {
      const response = await claimsAPI.downloadPDF(id);
      downloadBlob(response.data, `AdvanceClaim_${claim.claim_number}.pdf`);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (!claim) return null;

  const calc = claim.calculations || {};

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="claim-detail-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/claims')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold tracking-tight">{claim.claim_number}</h1>
              <Badge className={getStatusClass(claim.status)} data-testid="claim-status">
                {claim.status}
              </Badge>
            </div>
            <p className="text-muted-foreground">
              Created on {formatDate(claim.date_of_claim)}
            </p>
          </div>
          
          {/* Actions */}
          <div className="flex gap-2">
            {claim.status === 'LIVE' && (
              <>
                <Button onClick={handleApply} disabled={actionLoading} data-testid="apply-btn">
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Submit for Approval
                </Button>
                <Button variant="destructive" onClick={handleCancel} disabled={actionLoading} data-testid="cancel-btn">
                  <XCircle className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </>
            )}
            {['SENT FOR APPROVAL', 'APPROVED', 'PAID'].includes(claim.status) && (
              <Button variant="outline" onClick={handleDownloadPDF} disabled={actionLoading} data-testid="download-btn">
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Download PDF
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Travel Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Travel Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Travel Date</p>
                    <p className="font-medium">{formatDate(claim.date_of_travel)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Starting District</p>
                    <p className="font-medium">{claim.travel_start_district}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Project Site</p>
                    <p className="font-medium">{claim.project_site_location}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Sites Planned</p>
                    <p className="font-medium">{claim.no_of_sites_planned}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Expected Time</p>
                    <p className="font-medium">{claim.total_expected_travel_time} hours</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Expected Distance</p>
                    <p className="font-medium">{claim.total_expected_travel_distance} km</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Vehicle Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Car className="w-5 h-5" />
                  Vehicle Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Ownership</p>
                    <p className="font-medium">{claim.vehicle_ownership}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Vehicle Type</p>
                    <p className="font-medium">{claim.vehicle_type}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Eligible Persons */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Eligible Persons ({claim.no_of_eligible_persons})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {claim.eligible_persons?.map((person, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium">{person.name}</p>
                        <p className="text-sm text-muted-foreground">{person.type}</p>
                      </div>
                      {person.sourcing_person && (
                        <Badge variant="outline">{person.sourcing_person}</Badge>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Cost Breakdown */}
          <div className="space-y-6">
            <Card className="bg-primary text-primary-foreground">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Advance Claim Amount
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-4xl font-bold" data-testid="claim-amount">
                  {formatCurrency(calc.advance_claim_amount)}
                </p>
                <p className="text-sm opacity-80 mt-1">75% of Total Budget</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Cost Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Fuel Cost</span>
                  <span className="font-medium">{formatCurrency(calc.total_fuel_cost)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Hired Vehicle Load (10%)</span>
                  <span className="font-medium">{formatCurrency(calc.hired_vehicle_load)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Pocket Expenses ({formatCurrency(calc.allowable_rate_per_person)}/person)
                  </span>
                  <span className="font-medium">{formatCurrency(calc.pocket_expenses)}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-lg font-bold">
                  <span>Total Budget</span>
                  <span>{formatCurrency(calc.total_budget_cost)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Status Timeline */}
            <Card>
              <CardHeader>
                <CardTitle>Status History</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <div>
                      <p className="font-medium">Created</p>
                      <p className="text-sm text-muted-foreground">{formatDate(claim.created_at)}</p>
                    </div>
                  </div>
                  
                  {claim.status !== 'LIVE' && claim.status !== 'CANCELLED' && (
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <div>
                        <p className="font-medium">Submitted</p>
                        <p className="text-sm text-muted-foreground">Sent for approval</p>
                      </div>
                    </div>
                  )}

                  {claim.status === 'APPROVED' && (
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <div>
                        <p className="font-medium">Approved</p>
                        <p className="text-sm text-muted-foreground">
                          By {claim.approved_by} on {formatDate(claim.approval_date)}
                        </p>
                      </div>
                    </div>
                  )}

                  {claim.status === 'PAID' && (
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <div>
                        <p className="font-medium">Paid</p>
                        <p className="text-sm text-muted-foreground">
                          {formatCurrency(claim.payment_amount)} via {claim.payment_mode}
                        </p>
                      </div>
                    </div>
                  )}

                  {claim.status === 'REJECTED' && (
                    <div className="flex items-center gap-3">
                      <XCircle className="w-5 h-5 text-red-500" />
                      <div>
                        <p className="font-medium">Rejected</p>
                        <p className="text-sm text-muted-foreground">{claim.rejection_reason}</p>
                      </div>
                    </div>
                  )}

                  {claim.status === 'CANCELLED' && (
                    <div className="flex items-center gap-3">
                      <XCircle className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="font-medium">Cancelled</p>
                        <p className="text-sm text-muted-foreground">By user</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
